import {
  DropdownButton,
  FlexBox,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import type {
  SearchFilterOperatorExistence,
  SearchFilterOperatorString,
  SearchFilters,
  SearchFilterValue,
  SearchFilterValueString,
} from 'lib-search-types';
import { SearchFilterLayout } from 'lib-search-ui';
import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';

import { ContactsFilterOptions } from '../../options/contactsFilterOptions';

const operators: {
  value: SearchFilterOperatorString | SearchFilterOperatorExistence;
  label: string;
}[] = [
  { value: 'is_one', label: 'any of the selected values' },
  { value: 'is_not_one', label: 'none of the selected values' },
  { value: 'is_empty', label: 'no value' },
  { value: 'is_not_empty', label: 'any value' },
];

type ContactsFilterCompanyProps = {
  filters: SearchFilters<'users'>;
  onChangeFilters: (filters: SearchFilters<'users'>) => void;
};

export const ContactsFilterCompany = memo(function ContactsFilterCompany({
  filters,
  onChangeFilters,
}: ContactsFilterCompanyProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const condition = useMemo(
    () =>
      filters.filters
        .filter((filter) => 'field' in filter)
        .find((filter) => filter.field === 'company_id'),
    [filters.filters],
  );

  const selectedOperator = (condition?.operator ?? 'is_one') as
    | SearchFilterOperatorString
    | SearchFilterOperatorExistence;

  const isExistenceOperator =
    selectedOperator === 'is_empty' || selectedOperator === 'is_not_empty';

  const [
    readSearchCompanies,
    {
      results: companies,
      isLoading,
      isFetching,
      isSuccess,
      hasNextPage,
      fetchNextPage,
    },
  ] = useLazyReadSearchInfiniteQuery<'companies'>();

  useEffect(() => {
    if (isOpen) {
      readSearchCompanies({
        scope: 'companies',
        sortBy: { field: 'name', order: 'asc' },
        query: debouncedSearchTerm || undefined,
      });
    }
  }, [isOpen, debouncedSearchTerm, readSearchCompanies]);

  useUpdateEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleToggle = useCallback((newIsOpen: boolean) => {
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      setTimeout(() => inputRef.current?.focus());

      if (listWrapperRef.current) {
        listWrapperRef.current.scrollTop = 0;
      }
    }
  }, []);

  const handleChange = useCallback(
    ({ selectedValues }: SelectionListChangeEvent<SearchFilterValue>) => {
      const otherFilters = filters.filters.filter(
        (filter) => 'field' in filter && filter.field !== 'company_id',
      );

      onChangeFilters({
        ...filters,
        filters:
          selectedValues.length > 0
            ? [
                ...otherFilters,
                {
                  field: 'company_id',
                  operator: selectedOperator,
                  values: selectedValues as SearchFilterValueString[],
                },
              ]
            : otherFilters,
      });
    },
    [filters, onChangeFilters, selectedOperator],
  );

  const handleChangeOperator = useCallback(
    (
      newOperator: SearchFilterOperatorString | SearchFilterOperatorExistence,
    ) => {
      const otherFilters = filters.filters.filter(
        (filter) => 'field' in filter && filter.field !== 'company_id',
      );

      const isNewExistence =
        newOperator === 'is_empty' || newOperator === 'is_not_empty';

      onChangeFilters({
        ...filters,
        filters: [
          ...otherFilters,
          {
            field: 'company_id',
            operator: newOperator,
            values: isNewExistence
              ? []
              : ((condition?.values ?? []) as SearchFilterValueString[]),
          },
        ],
      });
    },
    [condition?.values, filters, onChangeFilters],
  );

  const handleClear = useCallback(() => {
    const otherFilters = filters.filters.filter(
      (filter) => 'field' in filter && filter.field !== 'company_id',
    );
    onChangeFilters({ ...filters, filters: otherFilters });
  }, [filters, onChangeFilters]);

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    inputRef.current?.focus();
  }, []);

  return (
    <DropdownButton
      show={isOpen}
      onToggle={handleToggle}
      buttonProps={{
        variant: 'tertiary',
        label: condition ? condition.values.length || 1 : undefined,
      }}
      fixed
      dropdown={
        <SearchFilterLayout>
          <SearchFilterLayout.Row borderBottom>
            <InputText
              ref={inputRef}
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search companies..."
              suffix={{
                type: 'custom',
                value: (
                  <FlexBox alignItems="center" padding="0 100">
                    <IconButton
                      variant="tertiary"
                      icon={searchTerm ? 'close' : 'search'}
                      onClick={searchTerm ? handleClearSearch : undefined}
                    />
                  </FlexBox>
                ),
                noWrap: true,
              }}
            />
          </SearchFilterLayout.Row>

          <SearchFilterLayout.Row ref={listWrapperRef} noPadding scrollable>
            <SelectionList
              isMultiple
              isActive={isOpen}
              isDisabled={isExistenceOperator}
              selectedValues={condition?.values ?? []}
              onChange={handleChange}
              equalityFn={(a, b) => a.value === b.value}
              defaultActiveItemIndex={0}
            >
              {companies.map((company) => (
                <SearchFilterLayout.Item
                  key={company.id}
                  value={{ value: company.id }}
                  label={company.name}
                  highlight={debouncedSearchTerm}
                />
              ))}

              {isFetching && <SearchFilterLayout.Loading />}
              {isSuccess && !companies.length && <SearchFilterLayout.Empty />}

              {!isFetching && hasNextPage && (
                <Waypoint onEnter={fetchNextPage} />
              )}
            </SelectionList>
          </SearchFilterLayout.Row>

          {!isLoading && (
            <SearchFilterLayout.Row borderTop>
              <ContactsFilterOptions
                operators={operators}
                selectedOperator={selectedOperator}
                defaultOperator="is_one"
                isMissingSelectedValues={!condition?.values?.length}
                onChangeOperator={handleChangeOperator}
                onClear={handleClear}
              />
            </SearchFilterLayout.Row>
          )}
        </SearchFilterLayout>
      }
    >
      Company
    </DropdownButton>
  );
});
